import { Component, forwardRef } from 'react';
import { captureReactException } from '@sentry/react';
import Tone from '@/Components/Marketing/Tone';
import { TooltipProvider } from '@/Components/ui/tooltip';
import { GrantsOverrideContext } from '@/Hooks/useGrants';

/**
 * Every ability the app knows, in App\Services\Api\Auth\AbilityCatalog::ALL's
 * order: the set a member holding everything would be shared. The app's
 * gates read a subset of these (useGrants().can / reasonFor, GatedButton's
 * `ability`); holding the whole catalog means no vignette ever renders a lock
 * because a newer screen checks an ability this list forgot. A test holds it
 * to the PHP catalog.
 */
export const VIGNETTE_ABILITIES = Object.freeze([
    'templates:read', 'templates:write',
    'ontologies:read', 'ontologies:write',
    'passports:read', 'passports:write',
    'compositions:read', 'compositions:write',
    'imports:read', 'imports:write',
    'files:read', 'files:write',
    'team:read',
    'lab:read', 'lab:write',
    'events:read',
    'api_keys:read', 'api_keys:write',
    'design:read', 'design:write',
    'data_requests:read', 'data_requests:write',
    'suppliers:read', 'suppliers:write',
    'webhook_endpoints:read', 'webhook_endpoints:write',
    'team:write', 'members:write', 'roles:write',
    'releases:read', 'releases:write',
    'consul:use',
    'external_access:read', 'external_access:write',
    'partner_links:read', 'partner_links:write',
    'partner_work:write',
]);

/**
 * The `teamAccess` a vignette renders under: every ability team-wide (a null
 * scope), no suspension, a team that issues passports. Not the owner: owner-
 * only chrome is not what a product fragment shows.
 */
export const VIGNETTE_ACCESS = Object.freeze({
    grants: Object.freeze(Object.fromEntries(VIGNETTE_ABILITIES.map((ability) => [ability, null]))),
    suspension: Object.freeze({ level: 'none', source: null, canCreate: true }),
    issuesPassports: true,
    isOwner: false,
});

// Belt and braces for a browser without `inert`: anything that still lands
// focus inside a sealed vignette (a Tab press, a component focusing itself on
// mount) gives it straight back.
const refuseFocus = (event) => {
    if (typeof event.target?.blur === 'function') event.target.blur();
};

/**
 * Sentry's ErrorBoundary without its report dialog (R91: marketing pages load
 * neither the feedback widget nor the report dialog, and @sentry/react's
 * boundary brings `showReportDialog` along). The error is still reported as
 * that boundary reports it: captureReactException with React's component
 * stack, handled when there is a fallback to show.
 */
class VignetteBoundary extends Component {
    state = { failed: false };

    static getDerivedStateFromError() {
        return { failed: true };
    }

    componentDidCatch(error, errorInfo) {
        captureReactException(error, errorInfo, { mechanism: { handled: !!this.props.fallback } });
    }

    render() {
        return this.state.failed ? this.props.fallback : this.props.children;
    }
}

/**
 * The title of an app screen inside a vignette (the passport header's id,
 * the public masthead's name, a data request's passport), passed to the
 * app's component as its `titleAs`. The app titles its page with an h1; on
 * the marketing site the page has its own h1, so the screen's title is a
 * paragraph with the same classes, marked so a story's camera and the tests
 * can find it.
 */
export function VignetteTitle(props) {
    return <p data-vignette-title="" {...props} />;
}

/**
 * The boundary a product vignette renders the app's own components inside.
 *
 * - Grants: `access` replaces the page's `teamAccess` for everything inside
 *   (see GrantsOverrideContext), so a guest sees the components unlocked and a
 *   signed-in member does not see their own team's permissions leak in.
 * - Providers the app layout would give them: a light marketing Tone (the app
 *   has no dark mode, so a vignette is light even on a dark section) and a
 *   Radix TooltipProvider (a Tooltip throws without one).
 * - Sealed by default: the subtree is `inert` and `aria-hidden`, takes no
 *   pointer events and no text selection, and refuses focus, so the visitor
 *   cannot click, type or tab into it. The seal blocks interaction only:
 *   what a wrapped component does on its own when it mounts (an effect that
 *   fetches, a timer, a history write) still runs. So use the presentational
 *   parts (props in, markup out), never data-fetching containers, inside a
 *   vignette. `label` names what the vignette shows for assistive technology
 *   (the sealed content is hidden from it); pass it unless the surrounding
 *   copy already says the same.
 * - Never render a Radix portal primitive (Dialog, Popover, DropdownMenu,
 *   HoverCard, Tooltip, Drawer) open or controlled-open inside a vignette:
 *   its content portals into document.body, outside the inert seal.
 * - Kept out of search results' snippets (`data-nosnippet`): its text is
 *   fixture data (a fictional maker's passport), not what the page says.
 * - A vignette that throws renders `fallback` (nothing by default) and is
 *   reported, instead of taking the marketing page down with it.
 *
 * The forwarded ref lands on the outermost element (usePlayback's `ref`).
 */
const VignetteScope = forwardRef(function VignetteScope(
    { children, label, access = VIGNETTE_ACCESS, fallback = null, className, ...props },
    ref,
) {
    // `access={null}` must not fall through useGrants' `??` to the visitor's
    // own teamAccess: an explicit null means "the default", like undefined.
    const content = (
        <VignetteBoundary fallback={fallback}>
            <GrantsOverrideContext.Provider value={access ?? VIGNETTE_ACCESS}>
                <Tone tone="light">
                    <TooltipProvider delayDuration={250}>{children}</TooltipProvider>
                </Tone>
            </GrantsOverrideContext.Provider>
        </VignetteBoundary>
    );

    return (
        <div
            ref={ref}
            data-vignette="sealed"
            data-nosnippet=""
            role={label ? 'img' : undefined}
            aria-label={label || undefined}
            className={className}
            {...props}
        >
            {/* React 18 has no boolean `inert`; the empty string is the
                attribute's present form. */}
            <div inert="" aria-hidden="true" onFocusCapture={refuseFocus} className="pointer-events-none h-full select-none">
                {content}
            </div>
        </div>
    );
});

export default VignetteScope;
